/**
 * Demo task data for Workflow State View preview.
 * Used when "Demo Mode" is toggled on.
 * Remove this file once real workflow instances are executing.
 */

import { GqlTaskStatus, GqlTaskPriority, type TaskFieldsFragment } from '@/generated/graphql';

const now = new Date().toISOString();
const hoursAgo = (h: number) => new Date(Date.now() - h * 3600_000).toISOString();
const hoursFromNow = (h: number) => new Date(Date.now() + h * 3600_000).toISOString();

function makeTask(overrides: Partial<TaskFieldsFragment> & { name: string; status: GqlTaskStatus }): TaskFieldsFragment {
  return {
    __typename: 'Task',
    id: `demo-${overrides.name.replace(/\s+/g, '-').toLowerCase().slice(0, 20)}-${Math.random().toString(36).slice(2, 6)}`,
    parentId: null,
    tenantId: 'ferp-system',
    workflowInstanceId: 'demo-rcm-workflow-001',
    description: '',
    taskType: 'MANUAL',
    assignedActor: null,
    assignedAt: null,
    priority: GqlTaskPriority.NORMAL,
    startedAt: null,
    completedAt: null,
    createdAt: hoursAgo(4),
    updatedAt: now,
    version: 1,
    createdBy: '2_8',
    metadata: {},
    sequenceOrder: null,
    dependencies: [],
    dependents: [],
    scheduledStart: null,
    scheduledEnd: null,
    dueAt: null,
    slaId: null,
    estimatedDurationMs: null,
    durationMs: null,
    progressPercent: null,
    queueId: null,
    constraintIds: [],
    validationIds: [],
    errorMessage: null,
    retryCount: 0,
    maxRetries: 3,
    failureStrategy: 'RETRY',
    ...overrides,
  } as TaskFieldsFragment;
}

export const DEMO_TASKS: TaskFieldsFragment[] = [
  // PENDING (3)
  makeTask({
    name: 'Generate Patient Statement',
    description: 'Create and send patient billing statement',
    status: GqlTaskStatus.PENDING,
    priority: GqlTaskPriority.LOW,
    taskType: 'AUTOMATED',
    scheduledStart: hoursFromNow(4),
    scheduledEnd: hoursFromNow(5),
    createdAt: hoursAgo(1),
  }),
  makeTask({
    name: 'Audit Compliance Check',
    description: 'Verify coding compliance with CMS guidelines',
    status: GqlTaskStatus.PENDING,
    priority: GqlTaskPriority.CRITICAL,
    taskType: 'MANUAL',
    scheduledStart: hoursFromNow(2),
    scheduledEnd: hoursFromNow(4),
    dueAt: hoursFromNow(6),
    createdAt: hoursAgo(2),
  }),
  makeTask({
    name: 'Update Charge Master',
    description: 'Sync facility charge master with current rates',
    status: GqlTaskStatus.PENDING,
    priority: GqlTaskPriority.LOW,
    taskType: 'SYSTEM',
    scheduledStart: hoursFromNow(8),
    scheduledEnd: hoursFromNow(10),
    createdAt: hoursAgo(6),
  }),

  // READY (2)
  makeTask({
    name: 'Code Diagnosis (ICD-10)',
    description: 'Assign diagnosis codes per clinical notes',
    status: GqlTaskStatus.READY,
    priority: GqlTaskPriority.CRITICAL,
    taskType: 'MANUAL',
    scheduledStart: hoursFromNow(0.5),
    scheduledEnd: hoursFromNow(1),
    dueAt: hoursFromNow(2),
    estimatedDurationMs: '1800000',
    dependencies: ['demo-triage-patient'],
  }),
  makeTask({
    name: 'Validate CPT Codes',
    description: 'Ensure procedure codes match documentation',
    status: GqlTaskStatus.READY,
    priority: GqlTaskPriority.NORMAL,
    taskType: 'AUTOMATED',
    scheduledStart: hoursFromNow(1),
    scheduledEnd: hoursFromNow(1.5),
    estimatedDurationMs: '600000',
  }),

  // ASSIGNED (2)
  makeTask({
    name: 'Review EOB Response',
    description: 'Parse explanation of benefits from payer',
    status: GqlTaskStatus.ASSIGNED,
    priority: GqlTaskPriority.NORMAL,
    taskType: 'AUTOMATED',
    assignedActor: { id: 'dr-sarah-chen', displayName: 'Dr. Sarah Chen' },
    assignedAt: hoursAgo(1),
    scheduledStart: hoursAgo(0.5),
    scheduledEnd: hoursFromNow(1),
  }),
  makeTask({
    name: 'Submit Claim to Payer',
    description: 'Transmit formatted 837P claim electronically',
    status: GqlTaskStatus.ASSIGNED,
    priority: GqlTaskPriority.HIGH,
    taskType: 'AUTOMATED',
    assignedActor: { id: 'j-martinez', displayName: 'J. Martinez' },
    assignedAt: hoursAgo(0.5),
    scheduledStart: hoursAgo(0.25),
    scheduledEnd: hoursFromNow(0.5),
    dueAt: hoursFromNow(1),
  }),

  // IN_PROGRESS (4)
  makeTask({
    name: 'Triage Patient Record',
    description: 'Initial review of incoming patient documentation',
    status: GqlTaskStatus.IN_PROGRESS,
    priority: GqlTaskPriority.CRITICAL,
    taskType: 'MANUAL',
    assignedActor: { id: 'dr-sarah-chen', displayName: 'Dr. Sarah Chen' },
    scheduledStart: hoursAgo(2),
    scheduledEnd: hoursAgo(1),
    dueAt: hoursFromNow(0.5),
    startedAt: hoursAgo(1.5),
    progressPercent: 65,
    estimatedDurationMs: '3600000',
    durationMs: '5400000',
  }),
  makeTask({
    name: 'Verify Insurance Coverage',
    description: 'Check eligibility and benefits with payer',
    status: GqlTaskStatus.IN_PROGRESS,
    priority: GqlTaskPriority.HIGH,
    taskType: 'MANUAL',
    assignedActor: { id: 'a-patel', displayName: 'A. Patel' },
    scheduledStart: hoursAgo(1),
    scheduledEnd: hoursFromNow(0.5),
    dueAt: hoursFromNow(1),
    startedAt: hoursAgo(0.75),
    progressPercent: 30,
    estimatedDurationMs: '2400000',
  }),
  makeTask({
    name: 'Credential Verification',
    description: 'Verify provider NPI and credentials',
    status: GqlTaskStatus.IN_PROGRESS,
    priority: GqlTaskPriority.HIGH,
    taskType: 'AUTOMATED',
    assignedActor: { id: 'system', displayName: 'System' },
    scheduledStart: hoursAgo(0.5),
    scheduledEnd: hoursFromNow(0.25),
    startedAt: hoursAgo(0.25),
    progressPercent: 80,
    estimatedDurationMs: '900000',
  }),
  makeTask({
    name: 'Schedule Follow-up',
    description: 'Book patient follow-up per care plan',
    status: GqlTaskStatus.IN_PROGRESS,
    priority: GqlTaskPriority.NORMAL,
    taskType: 'MANUAL',
    assignedActor: { id: 'k-nguyen', displayName: 'K. Nguyen' },
    scheduledStart: hoursAgo(3),
    scheduledEnd: hoursAgo(1),
    startedAt: hoursAgo(2),
    progressPercent: 45,
  }),

  // BLOCKED (1)
  makeTask({
    name: 'Reconcile Payment',
    description: 'Match ERA payment to posted charges — awaiting payer response',
    status: GqlTaskStatus.BLOCKED,
    priority: GqlTaskPriority.HIGH,
    taskType: 'MANUAL',
    assignedActor: { id: 'm-lopez', displayName: 'M. Lopez' },
    scheduledStart: hoursAgo(4),
    scheduledEnd: hoursAgo(1),
    dueAt: hoursFromNow(2),
    startedAt: hoursAgo(3),
    errorMessage: 'Awaiting ERA 835 from United Healthcare',
    dependencies: ['demo-submit-claim'],
    estimatedDurationMs: '3600000',
  }),

  // COMPLETED (3)
  makeTask({
    name: 'Process Prior Authorization',
    description: 'Prior auth approved by payer',
    status: GqlTaskStatus.COMPLETED,
    priority: GqlTaskPriority.HIGH,
    taskType: 'AUTOMATED',
    assignedActor: { id: 'system', displayName: 'System' },
    scheduledStart: hoursAgo(6),
    scheduledEnd: hoursAgo(4),
    dueAt: hoursAgo(3),
    startedAt: hoursAgo(5),
    completedAt: hoursAgo(4),
    durationMs: '3600000',
    progressPercent: 100,
  }),
  makeTask({
    name: 'Release Claim Hold',
    description: 'Claims released for electronic submission',
    status: GqlTaskStatus.COMPLETED,
    priority: GqlTaskPriority.NORMAL,
    taskType: 'MANUAL',
    assignedActor: { id: 'j-martinez', displayName: 'J. Martinez' },
    scheduledStart: hoursAgo(7),
    scheduledEnd: hoursAgo(5),
    startedAt: hoursAgo(6),
    completedAt: hoursAgo(5.5),
    durationMs: '1800000',
    progressPercent: 100,
  }),
  makeTask({
    name: 'Appeal Denied Claim',
    description: 'Appeal submitted with supporting documentation',
    status: GqlTaskStatus.COMPLETED,
    priority: GqlTaskPriority.HIGH,
    taskType: 'MANUAL',
    assignedActor: { id: 'dr-sarah-chen', displayName: 'Dr. Sarah Chen' },
    scheduledStart: hoursAgo(10),
    scheduledEnd: hoursAgo(6),
    dueAt: hoursAgo(5),
    startedAt: hoursAgo(8),
    completedAt: hoursAgo(6),
    durationMs: '7200000',
    progressPercent: 100,
  }),

  // FAILED (1)
  makeTask({
    name: 'Auto-Adjudicate Claim',
    description: 'Automated claim adjudication rejected by rules engine',
    status: GqlTaskStatus.FAILED,
    priority: GqlTaskPriority.CRITICAL,
    taskType: 'AUTOMATED',
    assignedActor: { id: 'system', displayName: 'System' },
    scheduledStart: hoursAgo(3),
    scheduledEnd: hoursAgo(2),
    dueAt: hoursAgo(1),
    startedAt: hoursAgo(2),
    errorMessage: 'CPT/ICD mismatch: E11.9 does not support 99214 without modifier',
    retryCount: 2,
    maxRetries: 3,
  }),
];
